import React, { useCallback, useEffect, useState } from 'react';
import { Button, Form, Input, Modal, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { buscarClientes, mensajeError } from '../../api/feriasApi';
import type { BusquedaCliente, Criterio, CriteriosCliente, ResumenCliente } from '../../api/feriasApi';
import { FctAviso } from '../../fct/react';
import { motivoEnPantalla } from './motivos';
import { SIN_DATO } from './textos';

interface Props {
  idCaso: number;
  abierto: boolean;
  /** Los criterios con los que empiezan las cajas: los valores de la ficha. */
  criterios: Partial<CriteriosCliente>;
  /** Clientes que listar al abrir en lugar de buscar: los que comparten el código CL de la ficha. */
  iniciales: ResumenCliente[] | null;
  /** Carga este cliente en la columna de Gescom y cierra. */
  onElegir: (idCliente: number) => void;
  onCerrar: () => void;
}

/** Un cliente de la lista de resultados, con lo que sabe de él la búsqueda. */
interface Fila {
  id: number;
  codigo: string | null;
  nombreCompleto: string;
  provincia: string | null;
  telefonoPrincipal: string | null;
  emailPrincipal: string | null;
}

// Las cajas, en el orden de la búsqueda de «Alta de clientes».
const CAJAS: { criterio: Criterio; etiqueta: string; mono?: boolean }[] = [
  { criterio: 'codigo', etiqueta: 'Código CL', mono: true },
  { criterio: 'nombre', etiqueta: 'Nombre y apellidos' },
  { criterio: 'email', etiqueta: 'Email' },
  { criterio: 'telefono', etiqueta: 'Teléfono', mono: true },
  { criterio: 'movil', etiqueta: 'Móvil', mono: true },
];

/**
 * La búsqueda del cliente del caso en Gescom (#98 de fct-ferias, D35): una caja por criterio, rellenada
 * con la ficha, combinadas con O, cada una con sus coincidencias, y los clientes encontrados. Elegir uno
 * lo carga.
 */
export function BuscadorCliente({ idCaso, abierto, criterios, iniciales, onElegir, onCerrar }: Props) {
  const [formulario] = Form.useForm<Partial<CriteriosCliente>>();
  const [resultado, setResultado] = useState<BusquedaCliente | null>(null);
  const [filas, setFilas] = useState<Fila[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const buscar = useCallback(
    async (valores: Partial<CriteriosCliente>) => {
      setBuscando(true);
      try {
        const encontrado = await buscarClientes(idCaso, valores);
        setResultado(encontrado);
        setFilas(encontrado.candidatos);
        setError(null);
      } catch (e) {
        setError(motivoEnPantalla(mensajeError(e)));
      } finally {
        setBuscando(false);
      }
    },
    [idCaso],
  );

  // Al abrir: las cajas toman los valores de la ficha, y la lista muestra los clientes dados o una búsqueda.
  useEffect(() => {
    if (!abierto) {
      return;
    }
    setResultado(null);
    if (iniciales) {
      setFilas(iniciales.map((c) => ({ ...c, telefonoPrincipal: null, emailPrincipal: null })));
    } else {
      void buscar(criterios);
    }
  }, [abierto, criterios, iniciales, buscar]);

  const columnas: ColumnsType<Fila> = [
    { title: 'Código', dataIndex: 'codigo', className: 'fct-mono', render: (c: string | null) => c || SIN_DATO },
    { title: 'Cliente', dataIndex: 'nombreCompleto' },
    { title: 'Provincia', dataIndex: 'provincia', render: (p: string | null) => p ?? SIN_DATO },
    { title: 'Teléfono', dataIndex: 'telefonoPrincipal', className: 'fct-mono', render: (t: string | null) => t ?? SIN_DATO },
    { title: 'Email', dataIndex: 'emailPrincipal', render: (e: string | null) => e ?? SIN_DATO },
    {
      title: '',
      key: 'elegir',
      render: (_, fila) => (
        <Button size="small" onClick={() => onElegir(fila.id)}>
          Elegir
        </Button>
      ),
    },
  ];

  return (
    <Modal
      open={abierto}
      title="Buscar el cliente en Gescom"
      width={960}
      onCancel={onCerrar}
      footer={<Button onClick={onCerrar}>Cerrar</Button>}
      // Cada apertura vuelve a empezar de los valores de la ficha.
      destroyOnClose
    >
      <p>Encuentra los clientes que coinciden en cualquiera de los datos (no en todos a la vez).</p>
      <Form form={formulario} initialValues={criterios} layout="vertical" aria-label="Criterios de búsqueda" onFinish={(valores) => void buscar(valores)}>
        <div className="ferias-buscador__cajas">
          {CAJAS.map(({ criterio, etiqueta, mono }) => (
            <Form.Item
              key={criterio}
              name={criterio}
              label={etiqueta}
              extra={
                resultado
                  ? `${resultado.coincidencias[criterio]} ${resultado.coincidencias[criterio] === 1 ? 'coincidencia' : 'coincidencias'}`
                  : undefined
              }
            >
              <Input className={mono ? 'fct-mono' : undefined} />
            </Form.Item>
          ))}
        </div>
        <Button htmlType="submit" loading={buscando}>
          Buscar
        </Button>
      </Form>

      {error && (
        <FctAviso tipo="error" titulo="No se ha podido buscar">
          {error}
        </FctAviso>
      )}
      {resultado?.puedeEstarCortada && (
        <FctAviso tipo="aviso" titulo="Puede haber más clientes que coincidan">
          Solo se muestran los 50 primeros. Afine los datos de la búsqueda.
        </FctAviso>
      )}

      <Table<Fila>
        rowKey="id"
        size="small"
        loading={buscando}
        columns={columnas}
        dataSource={filas}
        pagination={false}
        locale={{ emptyText: 'Ningún cliente de Gescom coincide.' }}
        style={{ marginTop: 16 }}
      />
    </Modal>
  );
}
